import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Sparkles, ArrowRight, ArrowLeft, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';

/**
 * CustomPlanWizardModal — 4-step wizard that collects a topic, length,
 * cadence and optional objective from the user, then kicks off the
 * background LLM build. Deliberately keeps the state simple so the
 * user can rewind through steps without losing input.
 */

const LENGTHS = [
  { days: 3,  label: '3 days',  hint: 'Quick focus'      },
  { days: 7,  label: '7 days',  hint: 'One week'         },
  { days: 14, label: '14 days', hint: 'Two weeks'        },
  { days: 30, label: '30 days', hint: 'A month of depth' },
];

const CADENCES = [
  { id: 'daily',            label: 'Every day',       hint: 'One day of the plan each calendar day' },
  { id: 'every_other_day',  label: 'Every other day', hint: 'A rest day between each session'       },
  { id: 'weekdays',         label: 'Weekdays only',   hint: 'Mon-Fri, weekends off'                 },
  { id: 'weekly',           label: 'Once a week',     hint: 'One day of the plan per week'          },
];


export default function CustomPlanWizardModal({ open, onClose, onCreated, initialTopic = '', initialObjective = '' }) {
  const [step, setStep] = useState(0);
  const [topic, setTopic] = useState('');
  const [lengthDays, setLengthDays] = useState(7);
  const [cadence, setCadence] = useState('daily');
  const [objective, setObjective] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const topicInputRef = useRef(null);
  const objectiveInputRef = useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  React.useEffect(() => {
    // Reset on open so re-opening the modal never carries stale input
    if (open) {
      setStep(0);
      setTopic(initialTopic);
      setLengthDays(7);
      setCadence('daily');
      setObjective(initialObjective);
      setSubmitting(false);
    }
  }, [open, initialTopic, initialObjective]);

  // When the topic step opens, scroll the input into view once the
  // keyboard has had a beat to appear. Same for the objective step.
  useEffect(() => {
    if (!open) return;
    const target = step === 0 ? topicInputRef.current : step === 3 ? objectiveInputRef.current : null;
    if (!target) return;
    const t = setTimeout(() => {
      try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [open, step]);

  const focusScroll = (e) => {
    // Belt-and-suspenders: on focus, wait for the keyboard to open
    // then bring the field back into view. Handles the case where
    // the user tabs between fields or the keyboard toggles types.
    const el = e.currentTarget;
    setTimeout(() => {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
    }, 300);
  };

  if (!open) return null;

  const canAdvance =
    (step === 0 && topic.trim().length >= 3) ||
    (step === 1 && !!lengthDays) ||
    (step === 2 && !!cadence) ||
    step === 3;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/reading-plans/custom`,
        {
          topic: topic.trim(),
          length_days: lengthDays,
          cadence,
          objective: objective.trim() || null,
        },
        { withCredentials: true },
      );
      const eta = Math.max(1, Math.round((res.data?.estimated_seconds || 180) / 60));
      toast.success(`Eili is building your plan — check back in about ${eta} minute${eta === 1 ? '' : 's'}.`);
      onCreated?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || 'Could not start the build');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-3 sm:px-4 sm:items-center overflow-y-auto pt-20 pb-6 sm:py-8"
      style={{
        background: 'rgba(0,0,0,0.55)',
        // Top-anchor the sheet on mobile so the on-screen keyboard —
        // which always opens from the BOTTOM — can never cover the
        // active input. We use Tailwind's `pt-20` (80 px) fixed
        // top offset rather than `env(safe-area-inset-top)`, because
        // Android WebView (native app wrapper) does not reliably
        // populate that env variable — leaving the sheet crashed
        // into the status bar. 80 px comfortably clears any modern
        // Android / iOS notch or status bar. On sm+ we drop to
        // `py-8` and recentre with `items-center`.
      }}
      onClick={onClose}
      data-testid="custom-plan-wizard-backdrop"
    >
      <div
        className="w-full max-w-md rounded-3xl p-6 shadow-2xl relative"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="custom-plan-wizard-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full transition-all hover:bg-[var(--theme-hover)]"
          style={{ color: 'var(--theme-textSecondary)' }}
          aria-label="Close"
          data-testid="custom-plan-wizard-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--theme-accent)' }}>
            Step {step + 1} of 4
          </p>
        </div>

        {step === 0 && (
          <div data-testid="wizard-step-topic">
            <h3
              className="text-lg font-semibold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
            >
              What do you want to study?
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
              A topic, a book, a virtue, a life question — anything grounded in Scripture.
            </p>
            <input
              ref={topicInputRef}
              type="text"
              autoFocus
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onFocus={focusScroll}
              maxLength={140}
              placeholder="e.g. Trusting God through anxiety"
              className="w-full rounded-xl px-4 py-3 text-sm outline-none"
              style={{
                background: 'var(--glass-bg-active)',
                color: 'var(--theme-text)',
                border: '1px solid var(--glass-border)',
              }}
              data-testid="wizard-topic-input"
            />
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-length">
            <h3
              className="text-lg font-semibold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
            >
              How long should it run?
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
              You can always start another when this one finishes.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {LENGTHS.map((l) => {
                const active = lengthDays === l.days;
                return (
                  <button
                    key={l.days}
                    type="button"
                    onClick={() => setLengthDays(l.days)}
                    className="text-left rounded-xl px-3 py-3 transition-all"
                    style={{
                      background: active ? 'var(--theme-accent)' : 'var(--glass-bg-active)',
                      color: active ? '#fff' : 'var(--theme-text)',
                      border: `1px solid ${active ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                    }}
                    data-testid={`wizard-length-${l.days}`}
                  >
                    <p className="text-sm font-semibold leading-tight">{l.label}</p>
                    <p className="text-[11px] mt-0.5" style={{ opacity: active ? 0.9 : 0.7 }}>{l.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-cadence">
            <h3
              className="text-lg font-semibold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
            >
              How often would you like to study?
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
              We'll pace your reminders to match — the plan itself stays the same length.
            </p>
            <div className="space-y-2">
              {CADENCES.map((c) => {
                const active = cadence === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCadence(c.id)}
                    className="w-full text-left rounded-xl px-4 py-3 transition-all flex items-center gap-3"
                    style={{
                      background: active ? 'color-mix(in oklab, var(--theme-accent) 12%, transparent)' : 'var(--glass-bg-active)',
                      border: `1px solid ${active ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                    }}
                    data-testid={`wizard-cadence-${c.id}`}
                  >
                    <div
                      className="h-4 w-4 rounded-full shrink-0"
                      style={{
                        background: active ? 'var(--theme-accent)' : 'transparent',
                        border: `1.5px solid ${active ? 'var(--theme-accent)' : 'var(--theme-border)'}`,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{c.label}</p>
                      <p className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>{c.hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-objective">
            <h3
              className="text-lg font-semibold mb-1"
              style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-text)' }}
            >
              What do you hope to gain? <span className="text-xs font-normal" style={{ color: 'var(--theme-textSecondary)' }}>(optional)</span>
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
              Sharing an objective helps Eili shape the plan around what matters most to you.
            </p>
            <textarea
              ref={objectiveInputRef}
              rows={4}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              onFocus={focusScroll}
              maxLength={400}
              placeholder="e.g. Learn how Scripture speaks to worry and lean on God more each morning."
              className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
              style={{
                background: 'var(--glass-bg-active)',
                color: 'var(--theme-text)',
                border: '1px solid var(--glass-border)',
              }}
              data-testid="wizard-objective-input"
            />
            <div
              className="mt-4 rounded-xl p-3 text-xs leading-relaxed"
              style={{ background: 'var(--theme-accentLight)', color: 'var(--theme-text)' }}
            >
              <p className="font-semibold mb-1">Ready to build:</p>
              <p style={{ color: 'var(--theme-textSecondary)' }}>
                {topic || '—'} · {lengthDays} days · {CADENCES.find((c) => c.id === cadence)?.label}
              </p>
              <p className="mt-2 text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
                Building usually takes 1–5 minutes. You can keep using the app — we'll notify you when it's ready.
              </p>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-6 flex items-center gap-2">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              className="rounded-xl"
              style={{ borderColor: 'var(--theme-border)', color: 'var(--theme-text)' }}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <Button
              type="button"
              disabled={!canAdvance}
              onClick={() => setStep((s) => s + 1)}
              className="rounded-xl px-5"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              disabled={submitting}
              onClick={submit}
              className="rounded-xl px-5"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="wizard-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>
              ) : (
                <><Check className="h-4 w-4 mr-2" /> Start building</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
